import React, { useCallback, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, IconButton, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { fetchSurveyChart, MenuProps } from '../../Widgets/WidgetService';
import { configureStackedSplitBarChart } from '../../ChartConfiguration/ChartConfiguration';
import SurveyChartSummary from '../../../Models/Widget/SurveyChartSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { useWidgetContext } from '../../../hooks/widgetContext';
import '../Widgets.css';

const SurveySummary = () => {
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [encounterOptions, setEncounterOptions] = useState({});
    const [isEncounterGraphShow, setIsEncounterGraphShow] = useState<boolean>(false);
    const [surveySummary, setSurveySummary] = useState<SurveyChartSummary>();
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const history = useNavigate();
    const {
        surveySelectedProvider,
        setSurveySelectedProvider,
        surveySelectedFilter,
        setSurveySelectedFilter,
        surveyFromDate,
        setSurveyFromDate,
        surveyToDate,
        setSurveyToDate,
        surveyShowInActiveProviders,
        setSurveyShowInActiveProviders,
    } = useWidgetContext();
    const filteredProviders = (surveyShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const chartHeight = 500;

    const handleClick = (event) => {
        setShow(!show);
    };

    const dateFilterList = [
        { Id: 1, Value: 'Last 3 Months' },
        { Id: 2, Value: 'Last 6 Months' },
        { Id: 3, Value: 'Last 1 Year' },
        { Id: 4, Value: 'Custom' },
    ];

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isEncounterGraphShow) {
            setEncounterOptions(
                configureStackedSplitBarChart(
                    'Survey Summary',
                    'Survey Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    '',
                    false,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isEncounterGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (surveySelectedFilter !== TimePeriodFilterEnum.Custom || (Boolean(surveyFromDate) && Boolean(surveyToDate))) {
            const changeFilter = async () => {
                let start = surveyFromDate !== null ? new Date(surveyFromDate).toDateString() : null;
                let end = surveyToDate !== null ? new Date(surveyToDate).toDateString() : null;

                const data = await fetchSurveyChart(surveySelectedProvider, surveySelectedFilter, start, end, false);
                setSurveySummary(data);
            };

            setIsEncounterGraphShow(false);
            setEncounterOptions(
                configureStackedSplitBarChart(
                    'Survey Summary',
                    'Survey Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    '',
                    false,
                    chartHeight
                )
            );
            changeFilter();
        }

        if (surveySelectedFilter !== TimePeriodFilterEnum.Custom && Boolean(surveyFromDate) && Boolean(surveyToDate)) {
            setSurveyFromDate(null);
            setSurveyToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveySelectedProvider, surveySelectedFilter, surveyFromDate, surveyToDate, surveyShowInActiveProviders]);

    useEffect(() => {
        if (surveySummary) {
            let xAxisValues = [];
            let yAxisValues = [];
            let totAppointmentValues = [];
            let totSurveyValues = [];
            for (var data in surveySummary.SurveyChartData) {
                let dataInfo = surveySummary.SurveyChartData[data];
                let percentage = dataInfo.AppointmentCount > 0 ? (dataInfo.SurveyCount / dataInfo.AppointmentCount) * 100 : 0;

                let d = {
                    y: dataInfo.SurveyCount,
                    name: Math.round(percentage),
                };
                totSurveyValues.push(d);

                let d1 = {
                    y: dataInfo.AppointmentCount,
                    name: '',
                };
                totAppointmentValues.push(d1);

                xAxisValues.push(dataInfo.Date);
            }
            yAxisValues.push({ name: 'Complete Appointments', data: totAppointmentValues, color: '#4682B4', showInLegend: true });
            yAxisValues.push({ name: 'Complete Appointments Survey Sent', data: totSurveyValues, color: '#00a652', showInLegend: true });

            if (surveySummary.SurveyChartData.length === 0) {
                setEncounterOptions(
                    configureStackedSplitBarChart(
                        'Survey Summary',
                        'Survey Count',
                        'Month',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        '',
                        '',
                        false,
                        chartHeight
                    )
                );
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setEncounterOptions(configureStackedSplitBarChart('Survey Chart', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, '', '', false, chartHeight));
            }
            setIsEncounterGraphShow(true);
        }
    }, [surveySummary]);

    const changeFromDate = (date: any) => {
        setSurveyFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(surveyToDate) || new Date(surveyToDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setSurveyToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setSurveyToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(surveyFromDate) || new Date(surveyFromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setSurveyFromDate(startDate);
        }
    };

    const toggleInactiveHandler = (isChecked: boolean) => {
        setSurveyShowInActiveProviders(isChecked);
        setSurveySelectedProvider('All');
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/surveysummarydetail`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center" ref={ref}>
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Survey Summary
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="body2">Total complete appointments versus complete appointment surveys sent</Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        <Grid size={3.5}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Providers</InputLabel>
                                <Select
                                    value={surveySelectedProvider}
                                    label="Providers"
                                    name="Providers"
                                    onChange={(e) => setSurveySelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={surveyShowInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={2.7}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={surveySelectedFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSurveySelectedFilter(parseInt(e.target.value as any))}
                                >
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {surveySelectedFilter === TimePeriodFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 2.9 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(surveyFromDate) ? surveyFromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 2.9 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(surveyToDate) ? surveyToDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                        <Grid size={12} marginTop={1} sx={{ position: 'relative' }}>
                            {!isEncounterGraphShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : null}
                            <HighchartsReact highcharts={Highcharts} options={encounterOptions} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default SurveySummary;
